export const cleanEnvVar = (value: string | undefined): string => {
  if (!value || value === 'undefined') return '';
  return value.replace(/^["']|["']$/g, ''); // Remove leading/trailing quotes
};

// Safely access environment variables for browser compatibility
export const getEnvVar = (key: string): string | undefined => {
  // Vite exposes env vars on `import.meta.env`
  // @ts-ignore
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
    // @ts-ignore
    return import.meta.env[key];
  }
  // Check for process.env for Node.js environments (e.g., tests, backend)
  if (typeof process !== 'undefined' && process.env && process.env[key]) {
    return process.env[key];
  }
  return undefined;
};

// Environment variables with fallbacks and quote cleaning
const rawAppApi = getEnvVar('VITE_APP_API');
const cleanedAppApi = cleanEnvVar(rawAppApi);
const backendApi =
  cleanEnvVar(getEnvVar('VITE_WORDPRESS_API')) || 'http://localhost:8000/wp-json/ideoz/v1';
const platformUrl = cleanEnvVar(getEnvVar('VITE_PLATFORM_LINK')) || 'http://localhost:3000';
const appApi = cleanedAppApi || 'http://localhost:8001/';
const aiApi = (cleanEnvVar(getEnvVar('VITE_AI_API')) || 'http://localhost:8002').replace(
  /\/?$/,
  '/'
);
const platformLink = cleanEnvVar(getEnvVar('VITE_PLATFORM_LINK')) || 'https://ideoz.ai/';

// Log environment variables in development for debugging
const isDev =
  getEnvVar('VITE_NODE_ENV') === 'development' || getEnvVar('NODE_ENV') === 'development';
if (isDev) {
  console.log('🔧 API Configuration:', {
    rawAppApi,
    cleanedAppApi,
    appApi,
    backendApi,
    platformUrl,
    aiApi,
    platformLink,
    buildTarget: getEnvVar('VITE_BUILD_TARGET'),
    nodeEnv: getEnvVar('VITE_NODE_ENV') || getEnvVar('NODE_ENV'),
  });
}

// Note: All API calls automatically handle 401 errors through the global interceptor
// which will logout users and clear their session when unauthorized responses are received
export const ApiUrls = {
  // Platform Links
  platformLink: platformLink,

  // Auth endpoints
  login: `${appApi}auth/login`,
  register: `${appApi}auth/register`,
  socialLogin: `${appApi}auth/social-login`,
  socialLoginGoogleCallback: `${appApi}auth/social-login/google/callback`,
  generatePairingCode: `${appApi}auth/generate-pairing-key`,
  pairingCode: `${appApi}auth/pairing-key-access-token`,
  pairLoggedUser: `${appApi}auth/pair-logged-user`,
  platformUrl: platformUrl,
  userProfile: `${appApi}auth/get-user-data`,
  updateOnboardingShown: `${appApi}auth/update-onboarding-shown`,
  updateGracePeriodNoticeShown: `${appApi}auth/grace-period-notice-shown`,
  logoutUser: `${appApi}auth/logout`,
  refreshToken: `${appApi}auth/refresh`,
  forgotPassword: `${appApi}auth/forgot-password`,
  resetPassword: `${appApi}auth/reset-password`,
  createProject: `${appApi}projects`,
  // Direct FE → Flask (bypasses Nest — same pattern as SSE conversations).
  // Auth: user JWT via apiClient interceptor; Flask's token_required decodes it.
  extractDesignSystem: `${aiApi}design-system/extract`,
  createSampleProject: `${appApi}projects/sample/portfolio`,
  getProject: (projectId: string) => `${appApi}projects/${projectId}`,
  updateProject: (projectId: string) => `${appApi}projects/${projectId}`,
  deleteProject: (projectId: string) => `${appApi}projects/${projectId}`,
  getUserProjectsWithPagination: (limit: number = 50, page: number = 1) => {
    return `${appApi}projects?limit=${limit}&page=${page}`;
  },
  checkSiteAvailability: `${backendApi}/check-site-availability`,
  addEvent: `${appApi}metrics/add-event`,
  addAiEvent: `${appApi}metrics/add-ai-event`,
  addFeedback: `${appApi}metrics/add-feedback`,
  changePassword: `${appApi}auth/change-password`,
  addHint: `${appApi}metrics/add-hint`,
  hints: `${backendApi}/hints`,
  promptHints: `${appApi}prompt-hints/prompt-hint`,
  getPromptCode: `${aiApi}/prompt-code`,
  addTime: `${backendApi}/metrics/add-time`,
  userIP: `${backendApi}/metrics/add-visit`,
  getPoints: `${backendApi}/transactions/get-user-balance`,
  addTransaction: `${backendApi}/transactions/add-transaction`,
  chat: `${aiApi}/llm-interaction`,
  chats: `${appApi}chats`,
  tmpConversations: `${appApi}tmp-conversations`,
  projectConversations: (projectId: string) => `${appApi}projects/${projectId}/conversations`,
  // Startup-as-project: scope CRUD is mounted under /projects/:id/scopes/...
  // (see scope.controller.ts in the backend). Artifacts were never brought
  // back. Scope ↔ conversation linkage is handled in Phase 4.
  getStartupScopes: (projectId: string) => `${appApi}projects/${projectId}/scopes`,
  createScope: (projectId: string) => `${aiApi}projects/${projectId}/scopes`,
  // Bulk scope suggestion (SSE): analyze uploaded files → suggested scopes.
  suggestScopes: (projectId: string) => `${aiApi}projects/${projectId}/scope-suggestions`,
  attachmentsReady: (attachmentIds: string[]) =>
    `${aiApi}attachments/ready?ids=${encodeURIComponent(attachmentIds.join(','))}`,
  getScopeById: (projectId: string, scopeId: string) =>
    `${appApi}projects/${projectId}/scopes/${scopeId}`,
  updateScope: (projectId: string, scopeId: string) =>
    `${appApi}projects/${projectId}/scopes/${scopeId}`,
  deleteScope: (projectId: string, scopeId: string) =>
    `${appApi}projects/${projectId}/scopes/${scopeId}`,
  // Scope-conversation endpoints (scoped chat — API 2026-06-25)
  listScopeConversations: (
    projectId: string,
    scopeId: string,
    mode?: 'clarify' | 'prototyper' | 'strategy'
  ) =>
    mode
      ? `${appApi}projects/${projectId}/scopes/${scopeId}/conversations?mode=${mode}`
      : `${appApi}projects/${projectId}/scopes/${scopeId}/conversations`,
  createScopeConversation: (projectId: string, scopeId: string) =>
    `${aiApi}projects/${projectId}/scopes/${scopeId}/conversations`,
  // Non-streaming "create empty conversation" — lets the client bind chat
  // attachments before the first message (the streaming create requires a
  // user_message; this one only needs `mode`).
  createScopeConversationShell: (projectId: string, scopeId: string) =>
    `${aiApi}projects/${projectId}/scopes/${scopeId}/conversations/shell`,
  updateScopeConversation: (projectId: string, scopeId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/scopes/${scopeId}/conversations/${conversationId}`,
  // Integrations (appApi — broker-based OAuth). `initiate` is a full-page redirect
  // to the broker's consent flow; the others are normal JSON calls.
  userIntegrations: () => `${appApi}integration/user-integrations`,
  integrationInitiate: (provider: string) => `${appApi}integration/initiate/${provider}`,
  integrationDisconnect: () => `${appApi}integration/disconnect`,
  conversations: (conversationId: string) =>
    `${aiApi}conversations/${conversationId}/llm-interactions`,
  // ICE scores (aiApi — ICE-Scores-Frontend-Guide §2). Queue is async: POST
  // returns 202 and the client polls the scope GET. Reading never computes.
  evaluateScopeIce: (projectId: string, scopeId: string) =>
    `${aiApi}projects/${projectId}/scopes/${scopeId}/evaluate`,
  getScopeIce: (projectId: string, scopeId: string) =>
    `${aiApi}projects/${projectId}/scopes/${scopeId}/ice`,
  getProjectIceRanking: (projectId: string) => `${aiApi}projects/${projectId}/scopes/ice`,
  // Scope Intelligence findings panel (aiApi — Backend-SSE-Integration-Guide §6)
  getFindingsByScope: (scopeId: string, includeDismissed: boolean = false) =>
    `${aiApi}findings/scopes/${scopeId}?include_dismissed=${includeDismissed}`,
  resolveFinding: (findingId: string) => `${aiApi}findings/${findingId}/resolve`,
  tmpConversationsLLM: (conversationId: string) =>
    `${appApi}tmp-conversations/${conversationId}/tmp-llm-interactions`,
  tmpConversationsInteractions: (conversationId: string) =>
    `${appApi}tmp-conversations/${conversationId}/tmp-llm-interactions`,
  // Like endpoints for LLM interactions
  tmpLlmInteractionLike: (tmpLlmInteractionId: string) =>
    `${appApi}tmp-conversations/tmp-llm-interactions/${tmpLlmInteractionId}/like`,
  llmInteractionLike: (llmInteractionId: string) =>
    `${appApi}conversations/llm-interactions/${llmInteractionId}/like`,
  prototypingBuildSessionLlm: (llmInteractionId: string) =>
    `${appApi}conversations/llm-interactions/${llmInteractionId}/build-session`,
  prototypingBuildSessionTmp: (tmpLlmInteractionId: string) =>
    `${appApi}tmp-conversations/tmp-llm-interactions/${tmpLlmInteractionId}/build-session`,
  tokens: (id: string) => `${aiApi}/llm-interaction/${id}/tokens`,
  saveUserData: `${appApi}auth/change-user-data`,
  requestCost(requestId: string) {
    return `${aiApi}/request-cost/${requestId}`;
  },
  requestUpgrade: `${appApi}auth/request-upgrade`,
  acceptTerms: `${backendApi}/auth/accept-terms`,
  benchmark: `${aiApi}/benchmark-evaluation`,
  AiTranslation: `${aiApi}/translation`,
  deletionReasons: `${backendApi}/auth/get-settings`,
  deleteAccount: `${backendApi}/auth/delete-user`,
  checkVisitor: `${appApi}visitors/check`,
  projectMetadata: () => `${appApi}projects/metadata`,
  chatAttachments: `${appApi}attachments/chat-attachments/`,
  // File upload endpoints
  uploadProjectFiles: (projectId: string) => `${appApi}projects/${projectId}/attachments`,
  getProjectAttachments: (projectId: string) => `${appApi}projects/${projectId}/attachments`,
  deleteProjectAttachment: (fileId: string) => `${appApi}attachments/${fileId}`,
  // Scope attachments — new in Phase 3. POST/GET are nested under the project
  // (which owns the scope); DELETE is rooted because the client only has the
  // attachment id.
  uploadScopeAttachment: (projectId: string, scopeId: string) =>
    `${appApi}projects/${projectId}/scopes/${scopeId}/attachments`,
  getScopeAttachments: (projectId: string, scopeId: string) =>
    `${appApi}projects/${projectId}/scopes/${scopeId}/attachments`,
  deleteScopeAttachment: (attachmentId: string) => `${appApi}scope-attachments/${attachmentId}`,

  // Prototype endpoints
  updatePrototyperDemoLink: (id: number) => `${appApi}prototyper/${id}`,
  /** DELETE: unpublish one version (its `/p/<slug>/v<number>` link). */
  unpublishPrototypeVersion: (id: number, versionId: string) =>
    `${appApi}prototyper/${id}/versions/${encodeURIComponent(versionId)}`,
  getPrototyperBySlug: (slug: string) => `${appApi}prototyper/by-slug/${encodeURIComponent(slug)}`,
  getPrototyperById: (id: string | number) => `${appApi}prototyper/${id}`,

  // Public published-artifact page (`/a/:slug`). Only surviving
  // artifact-demo-links endpoint.
  getArtifactBySlug: (slug: string) => `${appApi}artifact-demo-links/by-slug/${slug}`,
  // New `/artifacts` CRUD on Nest — replaces the artifact_demo_links flow.
  // Insert is AI-owned (Celery job on SSE stream completion); Nest serves
  // GET / PUT / DELETE. All routes speak the `{ data: ... }` envelope.
  listScopeArtifacts: (scopeId: string) =>
    `${appApi}artifacts/by-scope/${encodeURIComponent(scopeId)}`,
  listStartupArtifacts: (projectId: string) =>
    `${appApi}artifacts/by-project/${encodeURIComponent(projectId)}`,
  listScopePrototypes: (scopeId: string) =>
    `${appApi}prototypes?scope_id=${encodeURIComponent(scopeId)}`,
  listStartupPrototypes: (projectId: string) =>
    `${appApi}prototypes?project_id=${encodeURIComponent(projectId)}`,
  getPrototypeById: (id: string) => `${appApi}prototypes/${id}`,
  listArtifactsByLlmInteraction: (llmInteractionId: string) =>
    `${appApi}artifacts?llm_interaction_id=${encodeURIComponent(llmInteractionId)}`,
  getArtifactById: (id: string) => `${appApi}artifacts/${id}`,
  updateArtifact: (id: string) => `${appApi}artifacts/${id}`,
  publishArtifact: (id: string) => `${appApi}artifacts/${id}/publish`,
  deleteArtifact: (id: string) => `${appApi}artifacts/${id}`,
  // WebContainer streaming prototype endpoints
  projectConversationBuildStream: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/build-stream`,
  /** Stop button: abort the in-flight Chef generation (a closed stream only ends the current step). */
  projectConversationBuildStreamAbort: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/build-stream/abort`,
  /** Prototype versions (backend `versions` module): roster, drafts, lease, verify, fail. */
  conversationPrototypes: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/prototypes`,
  prototypeVersions: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/versions`,
  prototypeVersion: (projectId: string, conversationId: string, versionId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/versions/${versionId}`,

  prototypeSnapshotPrepare: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/snapshot`,
  prototypeSnapshotFinalize: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/snapshot/finalize`,
  prototypeSnapshotIndex: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/snapshots`,
  prototypeSnapshotByInteraction: (
    projectId: string,
    conversationId: string,
    llmInteractionId: string
  ) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/snapshot/by-interaction/${llmInteractionId}`,
  prototypePublish: (projectId: string, conversationId: string) =>
    `${appApi}projects/${projectId}/conversations/${conversationId}/prototype/publish`,

  conversationsById: (id: string) => `${appApi}conversations/${id}`,
  addUserAction: `${appApi}auth/add-user-action`,
  cleanUserActions: `${appApi}auth/clean-user-actions`,
  newEvent: `${backendApi}/metrics/add-new-event`,

  // Analytics events endpoint (new system)
  events: `${appApi}events`,
  // Bug reports
  submitBugReport: `${appApi}bug-reports`,

  // Google Drive endpoints
  gdriveAuthStatus: `${appApi}gdrive/auth/status`,
  gdriveAuthUrl: (redirectUrl: string) =>
    `${appApi}gdrive/auth/auth-url?redirectUrl=${redirectUrl}`,
  gdriveOAuthCallback: (code: string, redirectUrl: string) =>
    `${appApi}gdrive/auth/oauth2-callback?code=${encodeURIComponent(code)}&redirectUrl=${encodeURIComponent(redirectUrl)}`,
  gdriveGetToken: `${appApi}gdrive/auth/token`,
  gdriveFolders: (folderId: string) => `${appApi}gdrive/folders/${folderId}`,
  setProjectGdriveFolder: (projectId: string, folderId: string) =>
    `${appApi}projects/${projectId}/gdrive-folder?folderId=${encodeURIComponent(folderId)}`,
  syncProjectGdriveFolder: (projectId: string) =>
    `${appApi}projects/${projectId}/sync-gdrive-folder`,
  syncProjectGdriveStream: (projectId: string) =>
    `${appApi}projects/${projectId}/gdrive/sync-gdrive-stream`,
  gdriveSyncProgress: (projectId: string) => `${appApi}projects/${projectId}/gdrive-sync-progress`,
  saveGDriveAttachment: (projectId: string) => `${appApi}projects/${projectId}/gdrive-attachment`,

  // Payment/Stripe endpoints
  getPlans: `${appApi}payments/plans`,
  getPaymentMethods: `${appApi}payments/payment-method`,
  getPaymentMethodsUrl: (returnUrl: string) =>
    `${appApi}payments/payment-method-url?returnUrl=${encodeURIComponent(returnUrl)}`,
  // Payment/Stripe endpoints
  getInvoices: `${appApi}payments/invoices`,
  getCheckoutUrl: (planId: string, successUrl: string, cancelUrl: string) =>
    `${appApi}payments/checkout-url?planId=${encodeURIComponent(planId)}&successUrl=${encodeURIComponent(successUrl)}&cancelUrl=${encodeURIComponent(cancelUrl)}`,
  downgrade: `${appApi}payments/downgrade`,
  cancelDowngrade: `${appApi}payments/cancel-downgrade`,
  // Project bulk operations
  bulkUpdateProjects: `${appApi}projects/bulk-update`,
};

// Export base URLs for direct use
export const API_BASE_URLS = {
  app: appApi,
  backend: backendApi,
  ai: aiApi,
} as const;
